import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useTranslation } from '@/hooks/useTranslation';
import type { CommonTranslationKey } from '@/i18n/types';
import { cn } from '@/lib/utils';
import { Check, Loader2, Trash2, X } from 'lucide-react';
import React from 'react';

export interface DeleteButtonProps<T = unknown> {
  /** The entity object to be deleted */
  entity: T & { id: number };
  /** Whether the user can delete the entity */
  canDelete: boolean;
  /** Whether the popover is open */
  isPopoverOpen: boolean;
  /** Callback when popover open state changes */
  onPopoverChange: (open: boolean) => void;
  /** Callback when entity should be deleted */
  onDelete: (entity: T & { id: number }) => void;
  /** Whether the delete operation is in progress */
  isDeleting: boolean;
  /** Translation keys for delete action */
  deleteTranslationKeys: {
    confirmDelete: CommonTranslationKey;
    deleteButton?: CommonTranslationKey;
  };
  /** Optional custom class name */
  className?: string;
  /** Button variant */
  variant?: 'outline' | 'destructive' | 'ghost';
  /** Button size */
  size?: 'sm' | 'default' | 'lg';
  /** Whether to hide text on mobile/tablet (shows only icon) */
  hideTextOnMobile?: boolean;
}

/**
 * DeleteButton - A reusable component for deleting entities with confirmation
 *
 * This component provides a delete button that shows a confirmation popover
 * when clicked by authorized users.
 *
 * @example
 * ```tsx
 * <DeleteButton
 *   entity={role}
 *   canDelete={hasPermission(user, 'roles', 'delete')}
 *   isPopoverOpen={popoverOpenRole === role.id}
 *   onPopoverChange={(open) => setPopoverOpenRole(open ? role.id : null)}
 *   onDelete={handleDelete}
 *   isDeleting={deleteMutation.isPending}
 *   deleteTranslationKeys={{
 *     confirmDelete: 'roles.messages.confirmDelete',
 *     deleteButton: 'common.delete',
 *   }}
 * />
 * ```
 */
export const DeleteButton = <T,>({
  entity,
  canDelete,
  isPopoverOpen,
  onPopoverChange,
  onDelete,
  isDeleting,
  deleteTranslationKeys,
  className,
  variant = 'outline',
  size = 'sm',
  hideTextOnMobile = false,
}: DeleteButtonProps<T>): React.ReactElement => {
  const { t } = useTranslation();

  if (!canDelete) {
    return <></>;
  }

  return (
    <Popover open={isPopoverOpen} onOpenChange={onPopoverChange}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={cn(
            `gap-2 text-destructive hover:text-destructive`,
            className
          )}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Trash2 className='h-4 w-4' />
          )}
          {deleteTranslationKeys.deleteButton && (
            <span className={hideTextOnMobile ? 'hidden md:inline' : ''}>
              {t(deleteTranslationKeys.deleteButton)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-64 p-3'>
        <div className='flex flex-col space-y-3'>
          <p className='text-sm font-medium'>
            {t(deleteTranslationKeys.confirmDelete)}
          </p>
          <div className='flex justify-end space-x-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => onPopoverChange(false)}
              disabled={isDeleting}
              className='h-8 w-8 p-0'
              aria-label={t('common.actions.cancel')}
            >
              <X className='h-4 w-4' />
            </Button>
            <Button
              variant='destructive'
              size='sm'
              onClick={() => onDelete(entity)}
              disabled={isDeleting}
              className='h-8 w-8 p-0'
              aria-label={t('common.actions.confirmDelete')}
            >
              {isDeleting ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <Check className='h-4 w-4' />
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

DeleteButton.displayName = 'DeleteButton';
